<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  contactId: { type: [String, Number], default: null },
  contact: { type: Object, default: null },  // SZ: fallback quando store nao tem cached
  variant: { type: String, default: 'icon' },
});

const store = useStore();
const showConfirmDialog = ref(false);
const callerNumber = ref('');
const loadingCallerNumber = ref(false);

// ── Estado de ligação ativa (window._szCallState.active) ─────────────
// Sincroniza via CustomEvent emitido pelo próprio código abaixo nas funções
// que criam/destroem window._szCallState. Sem polling.
const isCallActive = ref(false);
function syncCallActive() {
  isCallActive.value = !!window._szCallState?.active;
}
onMounted(() => {
  syncCallActive();
  window.addEventListener('sz-call-state-changed', syncCallActive);
});
onBeforeUnmount(() => {
  window.removeEventListener('sz-call-state-changed', syncCallActive);
});

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);
const currentUser = computed(() => store.getters.getCurrentUser);

const currentContact = computed(() => {
  // SZ: pega o id de qualquer fonte disponível
  const cid = props.contactId || props.contact?.id;
  if (cid) {
    // tenta os 2 getters do store (alguns retornam mais campos que outros)
    const a = store.getters['contacts/getContactById']?.(cid);
    if (a && (a.phone_number || a.phoneNumber || a.name)) return a;
    const b = store.getters['contacts/getContact']?.(cid);
    if (b && (b.phone_number || b.phoneNumber || b.name)) return b;
    // fallback: usa o que veio na prop mesmo que parcial
    if (props.contact) return props.contact;
    return a || b || {};
  }
  // Icon variant: fetch from selected chat
  const chat = currentChat.value;
  if (!chat?.meta?.sender?.id) return null;
  return store.getters['contacts/getContact'](chat.meta.sender.id);
});

const contactPhone = computed(() => {
  const contact = currentContact.value;
  if (!contact) return '';
  let phone = contact.phone_number || contact.phoneNumber || contact.phone || '';
  if (!phone && contact.additional_attributes) {
    phone = contact.additional_attributes.phone_number || contact.additional_attributes.phone || '';
  }
  // SZ: fallback usar sourceId do contactInbox WhatsApp (vem cru, ex: "5588997786410")
  if (!phone) {
    const inboxes = contact.contactInboxes || contact.contact_inboxes || [];
    const wa = inboxes.find(ci => {
      const ct = ci.inbox?.channelType || ci.inbox?.channel_type || '';
      return ct.toLowerCase().includes('whatsapp');
    });
    if (wa?.sourceId || wa?.source_id) {
      const sid = wa.sourceId || wa.source_id;
      // Adiciona prefixo + se for só dígitos (formato E.164 sem +)
      phone = /^\d+$/.test(sid) ? '+' + sid : sid;
    }
  }
  return phone;
});

const contactName = computed(() => {
  const contact = currentContact.value;
  if (!contact) return '';
  return contact.name || contact.available_name || contact.availableName || '';
});

const contactThumbnail = computed(() => {
  const contact = currentContact.value;
  return contact?.thumbnail || '';
});

const isGroupConversation = computed(() => {
  if (props.contactId) return false;
  const chat = currentChat.value;
  if (!chat) return true;
  if (chat.conversation_type === 1) return true;
  const channelType = chat.meta?.channel;
  if (channelType?.includes('Group')) return true;
  return false;
});

const isVisible = computed(() => {
  if (props.variant === 'button') return true;
  return contactPhone.value && !isGroupConversation.value;
});

const chatwootInstance = computed(() => {
  const host = window.location.hostname;
  // SZ_TOKYO_CALL_FIX_20260525: slugs precisam bater com twilio-voice-v2
  // chatwoot_instances DB (tokyo/brasa/chat/canguru) pra validar o token
  // do agente contra o Chatwoot certo. Sem isso o v2 cai no fallback
  // "primeira instância" (canguru) e gera 401 nas chamadas do Tokyo.
  if (host.includes('tokyo.')) return 'tokyo';
  if (host.includes('canguru.')) return 'canguru';
  if (host.includes('chat.')) return 'chat';
  if (host.includes('beta.')) return 'beta';
  if (host.includes('staging.')) return 'staging';
  if (host.includes('brasa.')) return 'brasa';
  return 'principal';
});

const userToken = computed(() => {
  const user = currentUser.value;
  return user?.access_token || '';
});

async function openConfirmDialog() {
  // SZ: se nao tem phone mas tem id, busca o contact full do store via action contacts/show
  if (!contactPhone.value) {
    const cid = props.contactId || props.contact?.id || currentContact.value?.id;
    if (cid) {
      try {
        await store.dispatch('contacts/show', { id: cid });
      } catch (_) {}
    }
  }
  if (!contactPhone.value) {
    if (props.variant === 'button') {
      // eslint-disable-next-line no-console
      console.warn('[SzCallButton] sem telefone apos fetch:', { contact: currentContact.value, propsContactJSON: JSON.stringify(props.contact), propsContactId: props.contactId });
      alert('Este contato não possui número de telefone.');
    }
    return;
  }
  showConfirmDialog.value = true;
  fetchCallerNumber();
}

function closeConfirmDialog() {
  showConfirmDialog.value = false;
}

async function fetchCallerNumber() {
  loadingCallerNumber.value = true;
  try {
    const resp = await fetch(
      `${window.location.origin}/twilio-voice/api/caller-number?account_id=${accountId.value}&instance=${chatwootInstance.value}`
    );
    const data = await resp.json();
    callerNumber.value = data.phone_number || '';
  } catch {
    callerNumber.value = '';
  }
  loadingCallerNumber.value = false;
}

function startCall() {
  showConfirmDialog.value = false;

  const phone = contactPhone.value;
  const name = contactName.value;

  // Create call bar
  createCallBar(phone, name);

  // Create hidden iframe for Twilio SDK
  const iframe = document.createElement('iframe');
  iframe.id = 'sz-softphone-iframe';
  iframe.src = '/twilio-voice/softphone';
  iframe.style.cssText = 'width:100%;height:80px;border:none;position:fixed;bottom:0;left:0;opacity:0.01;pointer-events:none;z-index:1;';
  iframe.allow = 'microphone; autoplay';

  const convMatch = window.location.pathname.match(/conversations\/(\d+)/);
  const convId = convMatch ? parseInt(convMatch[1], 10) : null;

  iframe.onload = () => {
    iframe.contentWindow.postMessage({
      type: 'softphone-config',
      phone,
      contactName: name,
      accountId: accountId.value,
      agentIdentity: (currentUser.value?.name || 'agent').replace(/\s+/g, '-').toLowerCase(),
      userToken: userToken.value,
      conversationId: convId,
      useNonoDigit: true,
      backendUrl: window.location.origin + '/twilio-voice',
    }, '*');
    setTimeout(() => {
      iframe.contentWindow.postMessage({ type: 'softphone-dial', useNonoDigit: true }, '*');
    }, 1500);
  };

  window._szCallState = {
    active: true,
    iframe,
    phone,
    contactName: name,
    status: 'connecting',
    seconds: 0,
    muted: false,
  };
  window.dispatchEvent(new CustomEvent('sz-call-state-changed'));

  document.body.appendChild(iframe);
}

function createCallBar(phone, name) {
  if (document.getElementById('sz-call-bar')) return;

  // Tema adaptativo: card INVERTE o tema do Chatwoot.
  // CW dark → card branco. CW light → card preto. Dropdown SEMPRE contrasta com o card.
  const isDark = document.documentElement.classList.contains('dark') || document.body.classList.contains('dark');
  const T = isDark ? {
    bar: '#FFFFFF', barShadow: '0 8px 32px rgba(0,0,0,0.25)',
    text: '#000000', textMuted: '#5B5B5B',
    pillBg: '#F5F5F5', pillBorder: '#D4D4D4', pillText: '#000000', pillIcon: '#000000',
    waveBg: 'rgba(0,0,0,0.08)',
    ddBg: '#262626', ddBorder: '#3a3a3a', ddText: '#FFFFFF', ddHeader: '#9CA3AF',
    ddHover: 'rgba(255,255,255,0.06)', ddSelected: 'rgba(56,142,60,0.18)',
    ddShadow: '0 8px 24px rgba(0,0,0,0.4)',
  } : {
    bar: '#1E1E1E', barShadow: '0 8px 32px rgba(0,0,0,0.4)',
    text: '#FFFFFF', textMuted: '#A1A1A1',
    pillBg: '#393939', pillBorder: '#4B4B4B', pillText: '#FFFFFF', pillIcon: '#FFFFFF',
    waveBg: 'rgba(255,255,255,0.12)',
    ddBg: '#FFFFFF', ddBorder: '#E5E7EB', ddText: '#000000', ddHeader: '#5B5B5B',
    ddHover: 'rgba(0,0,0,0.04)', ddSelected: 'rgba(56,142,60,0.10)',
    ddShadow: '0 8px 24px rgba(0,0,0,0.15)',
  };

  const bar = document.createElement('div');
  bar.id = 'sz-call-bar';
  bar.style.cssText = `position:fixed;bottom:16px;left:50%;transform:translateX(-50%);z-index:99998;display:flex;align-items:center;justify-content:space-between;gap:16px;padding:8px 16px 8px 8px;background:${T.bar};border-radius:999px;box-shadow:${T.barShadow};font-family:Inter,system-ui,sans-serif;width:auto;min-width:600px;max-width:1300px;height:64px;animation:szSlideUp .25s ease;`;

  bar.innerHTML = `
    <div style="display:flex;align-items:center;gap:12px;min-width:0;flex-shrink:1;">
      <div style="width:48px;height:48px;border-radius:999px;border:1px solid #F5F5F5;background:#388E3C;display:flex;align-items:center;justify-content:center;flex-shrink:0;overflow:hidden;">
        ${contactThumbnail.value
          ? `<img src="${contactThumbnail.value}" style="width:48px;height:48px;border-radius:999px;object-fit:cover;" />`
          : '<svg width="20" height="20" viewBox="0 0 24 24" fill="#fff"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>'
        }
      </div>
      <div style="display:flex;flex-direction:column;min-width:0;max-width:180px;">
        <div data-sz-bar-name="1" style="font-weight:500;font-size:16px;color:${T.text};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${name || phone}</div>
        <span id="sz-bar-status-text" style="font-size:12px;color:#fbbf24;">Conectando...</span>
      </div>
    </div>
    <div style="display:flex;align-items:center;gap:8px;">
      <div id="sz-bar-mic-pill" style="height:32px;border:1px solid ${T.pillBorder};border-radius:999px;background:${T.pillBg};display:flex;align-items:center;padding:0 8px;gap:4px;cursor:pointer;flex-shrink:0;" onclick="window._szShowDevDropdown && window._szShowDevDropdown('input', this)">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style="flex-shrink:0;"><path d="M5 8V4C5 3.20435 5.31607 2.44129 5.87868 1.87868C6.44129 1.31607 7.20435 1 8 1C8.79565 1 9.55871 1.31607 10.1213 1.87868C10.6839 2.44129 11 3.20435 11 4V8C11 8.79565 10.6839 9.55871 10.1213 10.1213C9.55871 10.6839 8.79565 11 8 11C7.20435 11 6.44129 10.6839 5.87868 10.1213C5.31607 9.55871 5 8.79565 5 8ZM13 8C13 7.86739 12.9473 7.74021 12.8536 7.64645C12.7598 7.55268 12.6326 7.5 12.5 7.5C12.3674 7.5 12.2402 7.55268 12.1464 7.64645C12.0527 7.74021 12 7.86739 12 8C12 9.06087 11.5786 10.0783 10.8284 10.8284C10.0783 11.5786 9.06087 12 8 12C6.93913 12 5.92172 11.5786 5.17157 10.8284C4.42143 10.0783 4 9.06087 4 8C4 7.86739 3.94732 7.74021 3.85355 7.64645C3.75979 7.55268 3.63261 7.5 3.5 7.5C3.36739 7.5 3.24021 7.55268 3.14645 7.64645C3.05268 7.74021 3 7.86739 3 8C3.00152 9.23909 3.46245 10.4336 4.29365 11.3525C5.12485 12.2715 6.26728 12.8496 7.5 12.975V15C7.5 15.1326 7.55268 15.2598 7.64645 15.3536C7.74021 15.4473 7.86739 15.5 8 15.5C8.13261 15.5 8.25979 15.4473 8.35355 15.3536C8.44732 15.2598 8.5 15.1326 8.5 15V12.975C9.73272 12.8496 10.8751 12.2715 11.7063 11.3525C12.5375 10.4336 12.9985 9.23909 13 8Z" fill="${T.pillIcon}"/></svg>
        <span id="sz-bar-mic-name" style="font-family:Inter,system-ui,sans-serif;font-weight:500;font-size:12px;color:${T.pillText};max-width:130px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">Microfone</span>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style="flex-shrink:0;"><path d="M4 6l4 4 4-4" stroke="${T.pillIcon}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
      </div>
      <div id="sz-bar-spk-pill" style="height:32px;border:1px solid ${T.pillBorder};border-radius:999px;background:${T.pillBg};display:flex;align-items:center;padding:0 8px;gap:4px;cursor:pointer;flex-shrink:0;" onclick="window._szShowDevDropdown && window._szShowDevDropdown('output', this)">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style="flex-shrink:0;"><path fill-rule="evenodd" clip-rule="evenodd" d="M3.33325 7.33335C3.33325 4.75602 5.42259 2.66669 7.99992 2.66669C10.5772 2.66669 12.6666 4.75603 12.6666 7.33335V8.69032C13.7973 8.85206 14.6666 9.82453 14.6666 11V11.1667C14.6666 12.3633 13.6965 13.3334 12.4999 13.3334C11.8556 13.3334 11.3333 12.811 11.3333 12.1667V7.33335C11.3333 5.4924 9.84087 4.00002 7.99992 4.00002C6.15897 4.00002 4.66659 5.4924 4.66659 7.33335V12.1667C4.66659 12.811 4.14425 13.3334 3.49992 13.3334C2.3033 13.3334 1.33325 12.3633 1.33325 11.1667V11C1.33325 9.82453 2.20249 8.85206 3.33325 8.69032V7.33335ZM3.33325 10.0569C2.94485 10.1942 2.66659 10.5646 2.66659 11V11.1667C2.66659 11.5698 2.95288 11.9061 3.33325 11.9834V10.0569ZM12.6666 10.0569V11.9834C13.047 11.9061 13.3333 11.5698 13.3333 11.1667V11C13.3333 10.5646 13.055 10.1942 12.6666 10.0569Z" fill="${T.pillIcon}"/></svg>
        <span id="sz-bar-spk-name" style="font-family:Inter,system-ui,sans-serif;font-weight:500;font-size:12px;color:${T.pillText};max-width:130px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">Fone</span>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style="flex-shrink:0;"><path d="M4 6l4 4 4-4" stroke="${T.pillIcon}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
      </div>
      <div id="sz-bar-wave-pill" style="width:80px;height:40px;border-radius:24px;background:${T.waveBg};position:relative;display:flex;align-items:center;">
        <div id="sz-bar-wave" style="display:flex;align-items:center;gap:1px;padding-left:10px;height:16px;opacity:0.3;"></div>
        <div id="sz-bar-mute" style="position:absolute;right:0;top:0;width:40px;height:40px;border-radius:24px;background:#388E3C;display:flex;align-items:center;justify-content:center;cursor:pointer;" onclick="window._szToggleMute && window._szToggleMute()">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M19 10v2a7 7 0 01-14 0v-2" stroke="#fff" stroke-width="2" stroke-linecap="round"/><rect x="9" y="1" width="6" height="13" rx="3" stroke="#fff" stroke-width="2"/><line x1="12" y1="19" x2="12" y2="23" stroke="#fff" stroke-width="2" stroke-linecap="round"/><line x1="8" y1="23" x2="16" y2="23" stroke="#fff" stroke-width="2" stroke-linecap="round"/></svg>
        </div>
      </div>
      <div id="sz-bar-hangup" style="width:80px;height:40px;border-radius:24px;background:#FB2C36;display:flex;align-items:center;justify-content:center;cursor:pointer;" onclick="window._szHangup && window._szHangup()">
        <svg width="20" height="20" viewBox="0 0 17 17" fill="none" style="transform:rotate(135deg)"><path fill-rule="evenodd" clip-rule="evenodd" d="M5.25512 11.3847C8.45996 14.5895 11.429 14.9404 12.3004 14.9725C12.4262 14.9771 12.5725 14.9572 12.7611 14.91L12.8227 14.8946C13.7165 14.6712 14.4678 14.0776 14.8935 13.2698C14.1522 12.3997 13.1865 11.7249 12.1284 10.9932L11.0351 12.0865C10.7033 12.4183 10.1882 12.5539 9.71097 12.3609C9.12911 12.1255 7.69061 11.4631 6.43363 10.2062C5.17665 8.94918 4.51427 7.51068 4.27894 6.92882C4.08591 6.45157 4.2215 5.93648 4.55333 5.60465L5.64932 4.50866C5.63213 4.48338 5.61498 4.45815 5.59787 4.43297C4.88674 3.3867 4.23391 2.42621 3.38605 1.73861C2.56901 2.16603 1.96845 2.92392 1.7429 3.82612L1.72976 3.87869C1.6826 4.06732 1.66268 4.21357 1.66731 4.33938C1.69937 5.21077 2.05028 8.17983 5.25512 11.3847ZM12.2391 16.638C11.0383 16.5938 7.63777 16.1243 4.07661 12.5632C0.51545 9.00202 0.0459564 5.60146 0.00176641 4.40068C-0.0108788 4.05706 0.0460789 3.74156 0.112851 3.47447L0.125995 3.42189C0.498203 1.93306 1.54504 0.703851 2.95561 0.0993221C3.33566 -0.0635586 3.79598 -0.0337501 4.15988 0.231692C5.36148 1.10818 6.22889 2.3903 6.96767 3.48228C7.05259 3.60779 7.1358 3.73079 7.21763 3.85037C7.55581 4.34459 7.49997 5.01504 7.07062 5.44438L5.9403 6.5747C6.20264 7.15414 6.73787 8.15338 7.61214 9.02765C8.48641 9.90192 9.48565 10.4371 10.0651 10.6995L11.1949 9.56972C11.6246 9.13999 12.2979 9.08333 12.7933 9.42655C12.8799 9.48654 12.9675 9.54703 13.0561 9.60811C14.1779 10.3818 15.4405 11.2526 16.3796 12.4553C16.6643 12.8199 16.7066 13.2966 16.5368 13.6927C15.9346 15.0979 14.7101 16.1408 13.2269 16.5115L13.1653 16.5269C12.8982 16.5937 12.5827 16.6507 12.2391 16.638Z" fill="white"/></svg>
      </div>
    </div>
    <div style="display:flex;align-items:center;gap:16px;flex-shrink:0;">
      <div style="width:32px;height:32px;background:#388E3C;border-radius:6.4px;box-shadow:0 0 0 3.2px rgba(94,255,172,0.1);display:flex;align-items:center;justify-content:center;">
        <svg width="22" height="14" viewBox="0 0 22 14" fill="none"><path d="M10.8811 2.29692V0.308123C10.8811 0.137951 11.0245 0 11.2014 0H20.8688C21.0457 0 21.1891 0.137952 21.1891 0.308123V2.28061C21.1891 2.30945 21.1799 2.33759 21.1626 2.36118L17.4991 7.37192C17.45 7.43905 17.37 7.47899 17.2847 7.47899H14.8503C14.568 7.47899 14.4022 7.17362 14.5635 6.95077L17.7094 2.60504H11.2014C11.0245 2.60504 10.8811 2.46709 10.8811 2.29692Z" fill="white"/><path d="M7.35773 10.7703L11.5891 4.89839C11.6381 4.83045 11.7186 4.78992 11.8045 4.78992H14.374C14.6237 4.78992 14.7774 5.05261 14.6478 5.25795L11.478 10.2801H20.0981C20.1941 10.2801 20.2803 10.3368 20.3152 10.4228L21.3091 12.8751C21.3985 13.0955 21.2295 13.3333 20.9836 13.3333H7.73628C7.52721 13.3333 7.35773 13.1703 7.35773 12.9692V10.7703Z" fill="white"/><path d="M6.31262 13.1591V10.585C5.39656 10.7647 3.64264 10.823 2.56766 9.00966C2.46003 8.82811 2.22234 8.75069 2.03142 8.85044L0.195516 9.80964C0.0140288 9.90446-0.0549653 10.1226 0.0479192 10.2949C1.94296 13.4679 4.3313 13.3348 6.31262 13.1591Z" fill="white"/><path d="M9.00732 6.76015L7.3302 9.17109C7.20458 8.85084 6.65182 8.15596 5.4458 7.93843C4.20209 7.73601 1.56392 7.01212 0.96091 5.73596C0.423855 4.98065-0.214959 3.03317 1.52623 1.2857C2.66473 0.143088 4.4695-0.0491022 5.98285 0.0822787C7.46001 0.330077 9.01882 0.876262 10.0305 2.76069C10.1219 2.93096 10.0477 3.13814 9.87151 3.22757L8.01098 4.17192C7.83489 4.2613 7.61767 4.20288 7.50407 4.04559C7.1668 3.57863 6.7749 3.17343 6.5576 3.03498C5.4458 2.41866 4.1801 2.77818 3.84405 3.03498C3.70172 3.14375 3.34468 3.37034 3.34468 3.95042C3.34468 4.40722 4.01391 4.89327 4.38111 5.02899C6.29378 5.73596 7.73535 5.79941 9.00732 6.76015Z" fill="white"/></svg>
      </div>
      <div style="display:flex;align-items:center;gap:4px;">
        <div id="sz-bar-rec-dot" style="width:8px;height:8px;border-radius:50%;background:#F94B4B;animation:szBlink 1s infinite;display:none;"></div>
        <span id="sz-bar-timer" style="font-weight:600;font-size:12px;color:#F94B4B;font-variant-numeric:tabular-nums;display:none;">REC 00:00</span>
      </div>
    </div>
  `;

  // Add wave bars
  const waveWrap = bar.querySelector('#sz-bar-wave');
  for (let i = 0; i < 6; i++) {
    const wb = document.createElement('div');
    wb.style.cssText = `width:2px;background:#74C177;border-radius:24px;animation:szWave 0.8s ease-in-out infinite;animation-delay:${i * 0.12}s;`;
    waveWrap.appendChild(wb);
  }

  // Inject keyframes if not present
  if (!document.getElementById('sz-call-bar-styles')) {
    const style = document.createElement('style');
    style.id = 'sz-call-bar-styles';
    style.textContent = '@keyframes szSlideUp{from{transform:translateX(-50%) translateY(80px);opacity:0}to{transform:translateX(-50%) translateY(0);opacity:1}}@keyframes szWave{0%,100%{height:4px}50%{height:16px}}@keyframes szBlink{0%,100%{opacity:1}50%{opacity:0.3}}';
    document.head.appendChild(style);
  }

  document.body.appendChild(bar);

  // --- Tema reativo: observa toggle de class 'dark' no <html>/<body> e reaplica paleta ---
  window._szApplyBarTheme = () => {
    const barEl = document.getElementById('sz-call-bar');
    if (!barEl) return;
    const dark = document.documentElement.classList.contains('dark') || document.body.classList.contains('dark');
    const P = dark ? {
      bar: '#FFFFFF', barShadow: '0 8px 32px rgba(0,0,0,0.25)',
      text: '#000000', pillBg: '#F5F5F5', pillBorder: '#D4D4D4',
      pillText: '#000000', pillIcon: '#000000', waveBg: 'rgba(0,0,0,0.08)',
    } : {
      bar: '#1E1E1E', barShadow: '0 8px 32px rgba(0,0,0,0.4)',
      text: '#FFFFFF', pillBg: '#393939', pillBorder: '#4B4B4B',
      pillText: '#FFFFFF', pillIcon: '#FFFFFF', waveBg: 'rgba(255,255,255,0.12)',
    };
    barEl.style.background = P.bar;
    barEl.style.boxShadow = P.barShadow;
    const nameEl = barEl.querySelector('[data-sz-bar-name]');
    if (nameEl) nameEl.style.color = P.text;
    ['sz-bar-mic-pill', 'sz-bar-spk-pill'].forEach((id) => {
      const pill = document.getElementById(id);
      if (!pill) return;
      pill.style.background = P.pillBg;
      pill.style.borderColor = P.pillBorder;
      pill.querySelectorAll('svg path').forEach((p) => {
        const f = p.getAttribute('fill');
        if (f && f !== 'none') p.setAttribute('fill', P.pillIcon);
        const s = p.getAttribute('stroke');
        if (s && s !== 'none') p.setAttribute('stroke', P.pillIcon);
      });
    });
    ['sz-bar-mic-name', 'sz-bar-spk-name'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.style.color = P.pillText;
    });
    const wp = document.getElementById('sz-bar-wave-pill');
    if (wp) wp.style.background = P.waveBg;
  };

  if (window._szThemeObserver) {
    try { window._szThemeObserver.disconnect(); } catch {}
  }
  window._szThemeObserver = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === 'attributes' && m.attributeName === 'class') {
        window._szApplyBarTheme();
        break;
      }
    }
  });
  window._szThemeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  window._szThemeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

  // Setup global handlers
  window._szToggleMute = () => {
    const state = window._szCallState;
    if (!state) return;
    state.muted = !state.muted;
    const muteBtn = document.getElementById('sz-bar-mute');
    if (muteBtn) {
      muteBtn.style.background = state.muted ? '#ef4444' : '#388E3C';
    }
    if (state.iframe) {
      state.iframe.contentWindow.postMessage({ type: 'softphone-mute', muted: state.muted }, '*');
    }
  };

  window._szHangup = () => {
    const state = window._szCallState;
    if (state?.iframe) {
      state.iframe.contentWindow.postMessage({ type: 'softphone-hangup' }, '*');
    }
  };

  // --- Device pills (microfone / fone) ---
  // localStorage keys persistem a escolha entre chamadas.
  // postMessage para o iframe softphone aplica via Twilio.Device.audio.
  window._szDevices = window._szDevices || { input: [], output: [] };

  window._szLoadDevices = async () => {
    try {
      // Pede permissão pra ter labels (sem permissão, enumerateDevices devolve labels vazios)
      try {
        const tmp = await navigator.mediaDevices.getUserMedia({ audio: true });
        tmp.getTracks().forEach(t => t.stop());
      } catch {}
      const list = await navigator.mediaDevices.enumerateDevices();
      window._szDevices.input = list.filter(d => d.kind === 'audioinput');
      window._szDevices.output = list.filter(d => d.kind === 'audiooutput');

      const micId = localStorage.getItem('sz-mic-id') || 'default';
      const spkId = localStorage.getItem('sz-spk-id') || 'default';
      const micLbl = window._szDevices.input.find(d => d.deviceId === micId)?.label
        || window._szDevices.input[0]?.label || 'Microfone padrão';
      const spkLbl = window._szDevices.output.find(d => d.deviceId === spkId)?.label
        || window._szDevices.output[0]?.label || 'Fone padrão';
      const micNameEl = document.getElementById('sz-bar-mic-name');
      const spkNameEl = document.getElementById('sz-bar-spk-name');
      if (micNameEl) micNameEl.textContent = micLbl;
      if (spkNameEl) spkNameEl.textContent = spkLbl;

      // Aplica seleção persistida no iframe (best-effort — se iframe ainda nao tem Twilio.Device, ignora)
      const state = window._szCallState;
      if (state?.iframe?.contentWindow) {
        try { state.iframe.contentWindow.postMessage({ type: 'softphone-set-audio-device', kind: 'input', deviceId: micId }, '*'); } catch {}
        try { state.iframe.contentWindow.postMessage({ type: 'softphone-set-audio-device', kind: 'output', deviceId: spkId }, '*'); } catch {}
      }
    } catch (e) {
      // navegador sem suporte ou permissão negada — pill mantém label default
    }
  };

  window._szApplyDevice = (kind, deviceId, deviceLabel) => {
    const key = kind === 'input' ? 'sz-mic-id' : 'sz-spk-id';
    localStorage.setItem(key, deviceId);
    const nameEl = document.getElementById(kind === 'input' ? 'sz-bar-mic-name' : 'sz-bar-spk-name');
    if (nameEl) nameEl.textContent = deviceLabel;
    const state = window._szCallState;
    if (state?.iframe?.contentWindow) {
      try { state.iframe.contentWindow.postMessage({ type: 'softphone-set-audio-device', kind, deviceId }, '*'); } catch {}
    }
    const dd = document.getElementById('sz-bar-dev-dropdown');
    if (dd) dd.remove();
  };

  window._szShowDevDropdown = (kind, anchorEl) => {
    const existing = document.getElementById('sz-bar-dev-dropdown');
    if (existing) { existing.remove(); return; }
    const devices = kind === 'input' ? window._szDevices.input : window._szDevices.output;
    if (!devices || !devices.length) return;
    const selectedId = localStorage.getItem(kind === 'input' ? 'sz-mic-id' : 'sz-spk-id') || 'default';

    const rect = anchorEl.getBoundingClientRect();
    const dd = document.createElement('div');
    dd.id = 'sz-bar-dev-dropdown';
    dd.style.cssText = `position:fixed;left:${rect.left}px;bottom:${window.innerHeight - rect.top + 8}px;min-width:260px;max-width:360px;max-height:280px;overflow-y:auto;background:${T.ddBg};border:1px solid ${T.ddBorder};border-radius:8px;box-shadow:${T.ddShadow};padding:4px;z-index:99999;font-family:Inter,system-ui,sans-serif;`;

    const header = document.createElement('div');
    header.style.cssText = `padding:8px 10px 6px;font-size:11px;color:${T.ddHeader};font-weight:500;`;
    header.textContent = kind === 'input' ? 'Selecione a entrada de áudio' : 'Selecione a saída de áudio';
    dd.appendChild(header);

    devices.forEach(d => {
      const item = document.createElement('div');
      const isSel = d.deviceId === selectedId;
      item.style.cssText = `display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:6px;cursor:pointer;font-size:13px;color:${T.ddText};${isSel ? 'background:' + T.ddSelected + ';' : ''}`;
      item.onmouseenter = () => { if (!isSel) item.style.background = T.ddHover; };
      item.onmouseleave = () => { if (!isSel) item.style.background = 'transparent'; };
      item.innerHTML = '<span style="flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">' + (d.label || (kind === 'input' ? 'Microfone' : 'Fone')) + '</span>'
        + (isSel ? '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" style="flex-shrink:0;"><path d="M5 12l5 5L20 7" stroke="#22c55e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' : '');
      item.onclick = (ev) => {
        ev.stopPropagation();
        window._szApplyDevice(kind, d.deviceId, d.label || (kind === 'input' ? 'Microfone' : 'Fone'));
      };
      dd.appendChild(item);
    });

    document.body.appendChild(dd);

    // Fecha ao clicar fora
    setTimeout(() => {
      const onDocClick = (ev) => {
        if (!dd.contains(ev.target) && ev.target !== anchorEl && !anchorEl.contains(ev.target)) {
          dd.remove();
          document.removeEventListener('click', onDocClick);
        }
      };
      document.addEventListener('click', onDocClick);
    }, 0);
  };

  // Listen for softphone messages
  if (!window._szCallListener) {
    window._szCallListener = true;
    window.addEventListener('message', (event) => {
      const msg = event.data;
      if (!msg?.type?.startsWith('softphone-')) return;
      const state = window._szCallState;
      if (!state) return;

      switch (msg.type) {
        case 'softphone-status': {
          state.status = msg.state;
          const statusEl = document.getElementById('sz-bar-status-text');
          const waveEl = document.getElementById('sz-bar-wave');
          const recDot = document.getElementById('sz-bar-rec-dot');
          const timer = document.getElementById('sz-bar-timer');

          if (msg.state === 'connecting' && statusEl) {
            statusEl.textContent = 'Conectando...';
            statusEl.style.color = '#fbbf24';
          } else if (msg.state === 'ringing' && statusEl) {
            statusEl.textContent = 'Chamando...';
            statusEl.style.color = '#fbbf24';
          } else if (msg.state === 'connected' && statusEl) {
            statusEl.textContent = 'Em chamada';
            statusEl.style.color = '#22c55e';
            if (waveEl) waveEl.style.opacity = '1';
            if (recDot) recDot.style.display = 'block';
            if (timer) timer.style.display = 'block';
          } else if (msg.state === 'disconnected' || msg.state === 'error') {
            state.seconds = msg.duration || state.seconds;
            setTimeout(() => endCall(), 2000);
          }
          break;
        }
        case 'softphone-tick': {
          state.seconds = msg.seconds;
          const timer = document.getElementById('sz-bar-timer');
          if (timer) timer.textContent = 'REC ' + msg.formatted;
          break;
        }
      }
    });
  }

  // Popula labels das pills (best-effort)
  if (typeof window._szLoadDevices === 'function') {
    window._szLoadDevices();
  }
}

function endCall() {
  const state = window._szCallState;
  const contactN = state?.contactName || '';
  const duration = state?.seconds || 0;

  if (window._szThemeObserver) {
    try { window._szThemeObserver.disconnect(); } catch {}
    window._szThemeObserver = null;
  }

  const bar = document.getElementById('sz-call-bar');
  if (bar) bar.remove();
  const iframe = document.getElementById('sz-softphone-iframe');
  if (iframe) iframe.remove();

  // Show toaster if call had duration
  if (duration > 0) {
    showCallEndToaster(contactN, duration);
  }

  window._szCallState = null;
  window.dispatchEvent(new CustomEvent('sz-call-state-changed'));
}

function formatDuration(s) {
  if (!s) return '0:00';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return h + ':' + String(m).padStart(2,'0') + ':' + String(sec).padStart(2,'0');
  return m + ':' + String(sec).padStart(2,'0');
}

function showCallEndToaster(contactName, durationSeconds) {
  const existing = document.getElementById('sz-call-toaster');
  if (existing) existing.remove();

  const dur = formatDuration(durationSeconds);
  const name = contactName || 'contato';

  const toast = document.createElement('div');
  toast.id = 'sz-call-toaster';
  toast.style.cssText = 'position:fixed;bottom:24px;right:24px;z-index:99999;display:flex;flex-direction:column;gap:8px;background:#fff;border-radius:12px;padding:16px 20px;box-shadow:0 8px 32px rgba(0,0,0,0.15);font-family:Inter,system-ui,sans-serif;max-width:420px;min-width:320px;animation:szToastIn .3s ease;';

  toast.innerHTML = '<div style="display:flex;align-items:center;gap:8px;">'
    + '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" style="flex-shrink:0;"><circle cx="12" cy="12" r="10" fill="#388E3C"/><path d="M8 12l2.5 2.5L16 9" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    + '<span style="font-weight:600;font-size:15px;color:#000;flex:1;">Ligação encerrada</span>'
    + '<button onclick="this.closest(\'#sz-call-toaster\').remove()" style="border:none;background:none;cursor:pointer;padding:2px;display:flex;"><svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#9CA3AF" stroke-width="1.5" stroke-linecap="round"/></svg></button>'
    + '</div>'
    + '<div style="font-size:13px;line-height:20px;color:#4B5563;padding-left:30px;">A gravação e transcrição da ligação para <strong style=\'color:#388E3C\'>' + name + '</strong> estará disponível em notas de contato em alguns instantes</div>'
    + '<div style="font-size:12px;color:#6B7280;padding-left:30px;">Duração da ligação: ' + dur + '</div>';

  if (!document.getElementById('sz-toaster-styles')) {
    const style = document.createElement('style');
    style.id = 'sz-toaster-styles';
    style.textContent = '@keyframes szToastIn{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}';
    document.head.appendChild(style);
  }

  document.body.appendChild(toast);
  setTimeout(() => { if (toast.parentNode) toast.remove(); }, 8000);
}

// Beforeunload handler
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e) => {
    const state = window._szCallState;
    if (state?.active && state?.status === 'connected') {
      if (state.iframe) {
        try { state.iframe.contentWindow.postMessage({ type: 'softphone-hangup' }, '*'); } catch {}
      }
      e.preventDefault();
      e.returnValue = 'Você tem uma chamada ativa. Deseja sair?';
    }
  });
}
</script>

<template>
  <div v-if="isVisible" class="sz-call-button-wrap">
    <!-- Call Button -->
    <button
      v-if="variant === 'icon'"
      class="sz-call-btn"
      :class="{ 'sz-call-btn--active': isCallActive }"
      :title="isCallActive ? 'Ligação em andamento' : 'Iniciar ligação'"
      @click="openConfirmDialog"
    >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M17.3727 12.3797L13.6922 10.7305L13.682 10.7258C13.491 10.6441 13.2825 10.6113 13.0756 10.6304C12.8687 10.6495 12.6698 10.7198 12.4969 10.8352C12.4765 10.8486 12.457 10.8632 12.4383 10.8789L10.5367 12.5C9.33204 11.9148 8.08829 10.6805 7.50313 9.4914L9.12657 7.56093C9.14219 7.5414 9.15704 7.52187 9.1711 7.50078C9.28394 7.32836 9.35239 7.13071 9.37039 6.92544C9.38838 6.72017 9.35534 6.51363 9.27423 6.32421V6.31484L7.62032 2.62812C7.51309 2.38067 7.3287 2.17454 7.09468 2.04049C6.86067 1.90645 6.58958 1.85168 6.32188 1.88437C5.26326 2.02367 4.29155 2.54356 3.58824 3.34694C2.88492 4.15032 2.49809 5.18225 2.50001 6.24999C2.50001 12.4531 7.54688 17.5 13.75 17.5C14.8177 17.5019 15.8497 17.1151 16.6531 16.4118C17.4564 15.7084 17.9763 14.7367 18.1156 13.6781C18.1484 13.4105 18.0937 13.1395 17.9598 12.9055C17.8259 12.6715 17.62 12.487 17.3727 12.3797ZM13.75 16.25C11.0987 16.2471 8.55687 15.1926 6.68214 13.3179C4.8074 11.4431 3.7529 8.90127 3.75001 6.24999C3.74707 5.4871 4.02192 4.74921 4.52324 4.17414C5.02456 3.59907 5.71806 3.22614 6.47423 3.12499C6.47392 3.12811 6.47392 3.13125 6.47423 3.13437L8.11485 6.80624L6.50001 8.73906C6.48362 8.75792 6.46873 8.77803 6.45548 8.79921C6.33791 8.97962 6.26894 9.18733 6.25525 9.40223C6.24157 9.61713 6.28362 9.83191 6.37735 10.0258C7.08516 11.4734 8.54376 12.9211 10.007 13.6281C10.2023 13.721 10.4184 13.7616 10.634 13.746C10.8497 13.7304 11.0576 13.6591 11.2375 13.5391C11.2576 13.5255 11.2769 13.5109 11.2953 13.4953L13.1945 11.875L16.8664 13.5195C16.8664 13.5195 16.8727 13.5195 16.875 13.5195C16.7751 14.2768 16.4027 14.9717 15.8275 15.4742C15.2524 15.9768 14.5138 16.2526 13.75 16.25Z" fill="white"/></svg>
    </button>
    <!-- Button variant (contacts page) -->
    <slot v-if="variant === 'button'" name="trigger" :open="openConfirmDialog" />

    <!-- Confirmation Dialog -->
    <Teleport to="body">
      <div
        v-if="showConfirmDialog"
        class="sz-confirm-overlay"
        @click.self="closeConfirmDialog"
      >
        <div class="sz-confirm-modal">
          <div class="sz-confirm-header">
            <span class="sz-confirm-title">Confirmar ligação</span>
            <button class="sz-confirm-close" @click="closeConfirmDialog">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="#374151" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
          </div>
          <div class="sz-confirm-body">
            <div class="sz-confirm-subtitle">Você está prestes a realizar uma ligação para:</div>
            <div class="sz-confirm-contact-card">
              <div class="sz-confirm-avatar">
                <img v-if="contactThumbnail" :src="contactThumbnail" class="sz-confirm-avatar-img" />
                <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="#A1A1A1">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </div>
              <div class="sz-confirm-contact-info">
                <div class="sz-confirm-contact-name">{{ contactName || contactPhone }}</div>
                <div class="sz-confirm-contact-phone">{{ contactPhone }}</div>
                <div class="sz-confirm-caller-number" :class="{ 'sz-loading': loadingCallerNumber }">
                  {{ loadingCallerNumber ? 'Carregando numero de saida...' : (callerNumber ? 'Ligando de: ' + callerNumber : 'Nenhum numero atribuido') }}
                </div>
              </div>
            </div>
            <div class="sz-confirm-info-banner">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#236D23" stroke-width="2" />
                <line x1="12" y1="8" x2="12" y2="12" stroke="#236D23" stroke-width="2" stroke-linecap="round" />
                <circle cx="12" cy="16" r="1" fill="#236D23" />
              </svg>
              <span>A ligação será gravada e transcrita pelo sistema.</span>
            </div>
          </div>
          <div class="sz-confirm-footer">
            <button class="sz-btn-cancel" @click="closeConfirmDialog">Cancelar</button>
            <button class="sz-btn-confirm" @click="startCall">Iniciar ligação</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.sz-call-button-wrap {
  display: flex;
  align-items: center;
}
/* SZ Figma: botão de tel passa a ter visual neutro (igual aos outros do header). */
.sz-call-btn {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: transparent;
  border: 1.5px solid #D4D4D4;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: #000;
  transition: background 0.15s, border-color 0.15s;
}
.sz-call-btn svg path { fill: currentColor; }
.sz-call-btn:hover {
  background: rgba(0,0,0,0.04);
}

/* ── Ligação em andamento: verde + pulse no box-shadow ── */
@keyframes sz-call-pulse {
  0%   { box-shadow: 0 0 0 0    rgba(60, 178, 88, 0.55); }
  70%  { box-shadow: 0 0 0 8px  rgba(60, 178, 88, 0);    }
  100% { box-shadow: 0 0 0 0    rgba(60, 178, 88, 0);    }
}
.sz-call-btn--active {
  background: hsl(142 60% 50%);
  border-color: hsl(142 60% 50%);
  color: #fff;
  animation: sz-call-pulse 1.5s ease-out infinite;
}
.sz-call-btn--active:hover {
  background: hsl(142 60% 45%);
  border-color: hsl(142 60% 45%);
}
/* dark mode aplicado via solarz_override (scoped + :global nao funciona) */

/* Confirm Dialog */
.sz-confirm-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
}
.sz-confirm-modal {
  width: 493px;
  max-width: 95vw;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  font-family: Inter, system-ui, sans-serif;
  overflow: hidden;
}
.sz-confirm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
}
.sz-confirm-title {
  font-weight: 500;
  font-size: 20px;
  color: #000;
}
.sz-confirm-close {
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  display: flex;
}
.sz-confirm-body {
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 16px;
  border-top: 1px solid #D1D5DB;
  border-bottom: 1px solid #D1D5DB;
}
.sz-confirm-subtitle {
  font-size: 16px;
  color: #525252;
}
.sz-confirm-contact-card {
  display: flex;
  align-items: center;
  padding: 8px;
  gap: 8px;
  background: #FAFAFA;
  border-radius: 8px;
}
.sz-confirm-avatar {
  width: 56px;
  height: 56px;
  border-radius: 999px;
  background: #F5F5F5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}
.sz-confirm-avatar-img {
  width: 56px;
  height: 56px;
  border-radius: 999px;
  object-fit: cover;
}
.sz-confirm-contact-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.sz-confirm-contact-name {
  font-weight: 500;
  font-size: 16px;
  color: #000;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sz-confirm-contact-phone {
  font-size: 12px;
  color: #5B5B5B;
}
.sz-confirm-caller-number {
  font-size: 11px;
  color: #236D23;
}
.sz-confirm-caller-number.sz-loading {
  color: #888;
  font-style: italic;
}
.sz-confirm-info-banner {
  display: flex;
  align-items: center;
  padding: 8px;
  gap: 8px;
  background: #EEF6F0;
  border-radius: 8px;
  font-size: 12px;
  color: #236D23;
}
.sz-confirm-footer {
  display: flex;
  justify-content: flex-end;
  padding: 12px;
  gap: 12px;
}
.sz-btn-cancel {
  padding: 8px 16px;
  border: 1.5px solid #D4D4D4;
  border-radius: 8px;
  background: transparent;
  font-weight: 600;
  font-size: 14px;
  color: #000;
  cursor: pointer;
}
.sz-btn-cancel:hover {
  background: #f5f5f5;
}
.sz-btn-confirm {
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background: #388E3C;
  font-weight: 600;
  font-size: 14px;
  color: #fff;
  cursor: pointer;
}
.sz-btn-confirm:hover {
  background: #2E7D32;
}
</style>

<style>
/* Dark mode confirm dialog (unscoped) */
.dark .sz-confirm-overlay { background: rgba(0,0,0,0.6) !important; }
.dark .sz-confirm-modal { background: #1F2125 !important; border-color: #363840 !important; }
.dark .sz-confirm-header { border-bottom-color: #363840 !important; }
.dark .sz-confirm-title { color: #EDEEEF !important; }
.dark .sz-confirm-close svg { color: #9CA3AF !important; stroke: #9CA3AF !important; }
.dark .sz-confirm-body { color: #D4D4D4 !important; }
.dark .sz-confirm-subtitle { color: #D4D4D4 !important; }
.dark .sz-confirm-contact-card { background: #171717 !important; border-color: #363840 !important; }
.dark .sz-confirm-avatar { background: #363840 !important; color: #9CA3AF !important; }
.dark .sz-confirm-contact-name { color: #EDEEEF !important; }
.dark .sz-confirm-contact-phone { color: #9CA3AF !important; }
.dark .sz-confirm-caller-number { color: #7DBA80 !important; }
.dark .sz-confirm-info-banner { background: rgba(44,123,44,0.1) !important; color: #9CA3AF !important; }
.dark .sz-confirm-actions { border-top-color: #363840 !important; }
.dark .sz-btn-cancel { background: transparent !important; border-color: #363840 !important; color: #D4D4D4 !important; }
.dark .sz-btn-cancel:hover { background: #363840 !important; }
.dark .sz-btn-call { background: #2C7B2C !important; color: #fff !important; }
</style>
